import { Brand } from '@hertz/odinui-web'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Contract } from '@typings/api/contract.types'
import { ContractState } from '@typings/store/contractState.types'
import { validateLocalStorage } from '@utils/validateLocalStorage'
import { DateTime } from 'luxon'

import api from '../../services/api'
import getErrorCode from '../../services/getErrorCode'

type ContractFetchBody = {
  referrer: string
  uuid: string
}

const HttpUnauthorized = 401
const zone = 'UTC'
let refetchTimeout: NodeJS.Timeout | null = null

export const fetchContract = createAsyncThunk('contract/get', async (body: ContractFetchBody | null, thunkAPI) => {
  try {
    const response = await api.post<Contract>({
      url: '/terms/verify',
      data: body,
    })

    const contract = response.data
    const pickupDate = contract?.agreement?.checkOutDate

    await validateLocalStorage(contract?.agreement)

    if (!pickupDate) {
      return contract
    }

    const pickupDateMillis = DateTime.fromMillis(pickupDate, { zone })
    const now = DateTime.now().setZone(zone)
    const difference = pickupDateMillis.diff(now).milliseconds

    if (difference <= 0) {
      return contract
    }

    if (refetchTimeout != null) {
      clearTimeout(refetchTimeout)
    }

    // Fetch the contract again when the rental has started to get the express checkout code
    refetchTimeout = setTimeout(() => thunkAPI.dispatch(fetchContract(null)), difference + 100)

    return contract
  } catch (error) {
    return thunkAPI.rejectWithValue(getErrorCode(error))
  }
})

const initialState: ContractState = {
  brand: 'hertz',
  contractLoadingState: 'idle',
}

export const contractSlice = createSlice({
  name: 'contract',
  initialState,
  reducers: {
    resetContractLoadingState: (state) => {
      state.contractLoadingState = 'idle'
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContract.pending, (state) => {
        state.contractLoadingState = 'pending'
      })
      .addCase(fetchContract.fulfilled, (state, action) => {
        const contract = action.payload
        if (contract?.agreement?.voidedRaIndicator) {
          state.contractLoadingState = 'voided'
        } else if (contract?.creditCardMissing) {
          state.contract = contract
          state.contractLoadingState = 'missingCreditCard'
        } else {
          state.brand = (contract?.agreement?.brand?.toLowerCase() as Brand) || 'hertz'
          state.contractLoadingState = 'succeeded'
          state.contract = contract
        }
      })
      .addCase(fetchContract.rejected, (state, action) => {
        if (action.payload == HttpUnauthorized) {
          state.contractLoadingState = 'unauthorized'
        } else {
          state.contractLoadingState = 'failed'
        }
      })
  },
})

export const { resetContractLoadingState } = contractSlice.actions
export const contractReducer = contractSlice.reducer
